import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuizIcon from "@mui/icons-material/Quiz";
import GradeIcon from "@mui/icons-material/Grade";
import CategoryIcon from "@mui/icons-material/Category";
import { alpha } from "@mui/material/styles";
import { useTheme } from "styled-components";
import { styled } from "@mui/system";

const PreviewCard = styled(Paper)(({ theme }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  return {
    padding: "24px",
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(17, 24, 39, 0.08)",
    border: `1px solid ${alpha(primary, 0.1)}`,
    background: "#ffffff",
  };
});

const CriteriaCard = styled(Box)(({ theme }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  return {
    padding: "16px",
    borderRadius: 12,
    background: alpha(primary, 0.04),
    border: `1px solid ${alpha(primary, 0.12)}`,
  };
});

const CategoryChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isHighlighted",
})(({ theme, isHighlighted }) => {
  const primary = theme?.color4 || theme?.primary || "#20A6D2";
  return {
    padding: "12px 16px",
    borderRadius: 10,
    background: isHighlighted ? alpha(primary, 0.15) : alpha(primary, 0.06),
    border: `1px solid ${isHighlighted ? primary : alpha(primary, 0.15)}`,
    minHeight: 80,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };
});

const RubricsPreview = ({ questions = [], assessmentName = "" }) => {
  const theme = useTheme();
  const primaryColor = theme?.color4 || theme?.primary || "#20A6D2";

  const totalMarks = questions.reduce((sum, q) => sum + (q.totalMarks || 0), 0);
  const totalQuestions = questions.length;

  if (questions.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          borderRadius: 16,
          border: `1px dashed ${alpha(primaryColor, 0.24)}`,
          minHeight: "50vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: alpha(primaryColor, 0.02),
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            backgroundColor: alpha(primaryColor, 0.1),
            color: primaryColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <QuizIcon sx={{ fontSize: 36 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          No questions to preview
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Add questions in the Questionnaire tab to see the preview here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Summary Header */}
      <PreviewCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} color={theme?.color4}>
              {assessmentName || "Rubrics Assessment Preview"}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Review all questions and their rubric criteria
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Chip
              icon={<QuizIcon />}
              label={`${totalQuestions} Question${totalQuestions !== 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
            <Chip
              icon={<GradeIcon />}
              label={`${totalMarks} Total Marks`}
              color="secondary"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
        </Stack>
      </PreviewCard>

      {/* Questions List */}
      {questions.map((question, qIdx) => (
        <Accordion
          key={question.id || qIdx}
          defaultExpanded
          sx={{
            borderRadius: "16px !important",
            boxShadow: "0 4px 20px rgba(17, 24, 39, 0.06)",
            border: `1px solid ${alpha(primaryColor, 0.1)}`,
            "&:before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: alpha(primaryColor, 0.03),
              borderBottom: `1px solid ${alpha(primaryColor, 0.08)}`,
              "& .MuiAccordionSummary-content": {
                my: 2,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2} width="100%">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: primaryColor,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                {qIdx + 1}
              </Box>
              <Box flex={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {question.text || "Untitled Question"}
                </Typography>
                <Stack direction="row" spacing={1} mt={0.5}>
                  <Chip
                    size="small"
                    label={`${question.totalMarks || 0} marks`}
                    sx={{
                      backgroundColor: alpha(primaryColor, 0.1),
                      color: primaryColor,
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                  {question.rubricType && (
                    <Chip
                      size="small"
                      icon={<CategoryIcon sx={{ fontSize: 14 }} />}
                      label={question.rubricType}
                      variant="outlined"
                      sx={{ fontSize: 11 }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3 }}>
            {question.rubricData && question.rubricData.length > 0 ? (
              <Stack spacing={3}>
                {question.rubricData.map((criteriaEntry, cIdx) => (
                  <CriteriaCard key={`${question.id}-criteria-${cIdx}`}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: primaryColor,
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={700} color={theme?.color3}>
                        {criteriaEntry.criteria || `Criteria ${cIdx + 1}`}
                      </Typography>
                    </Stack>

                    <Grid container spacing={2}>
                      {criteriaEntry.categories && criteriaEntry.categories.length > 0 ? (
                        criteriaEntry.categories.map((cat, catIdx) => (
                          <Grid item xs={12} sm={6} md={4} key={`${criteriaEntry.criteria}-cat-${catIdx}`}>
                            <CategoryChip isHighlighted={catIdx === 0}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" fontWeight={700} color={theme?.color3}>
                                  {cat.category || "General"}
                                </Typography>
                                {cat.percentage && (
                                  <Chip
                                    size="small"
                                    label={cat.percentage}
                                    sx={{
                                      height: 20,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      backgroundColor: primaryColor,
                                      color: "#fff",
                                    }}
                                  />
                                )}
                              </Stack>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  fontSize: 12,
                                  lineHeight: 1.5,
                                  mt: 0.5,
                                }}
                              >
                                {cat.instruction || "No instruction provided"}
                              </Typography>
                            </CategoryChip>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            No categories defined for this criteria.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CriteriaCard>
                ))}
              </Stack>
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: alpha(primaryColor, 0.02),
                  borderRadius: 12,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No rubric criteria defined for this question.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Footer Summary */}
      {/* <PreviewCard sx={{ backgroundColor: alpha(primaryColor, 0.02) }}> */}
        {/* <Divider sx={{ mb: 2 }} /> */}
        {/* <Stack direction="row" justifyContent="end" alignItems="center"> */}
          {/* <Typography variant="body2" color="text.secondary">
            Assessment Summary
          </Typography> */}
          {/* <Stack direction="row" spacing={3}>
            <Typography variant="body2">
              <strong>Questions:</strong> {totalQuestions}
            </Typography>
            <Typography variant="body2">
              <strong>Total Marks:</strong> {totalMarks}
            </Typography>
          </Stack>
        </Stack> */}
      {/* </PreviewCard> */}
    </Box>
  );
};

export default RubricsPreview;

